import { type CSSProperties, type ReactNode, type RefObject, useMemo } from 'react';

import { GLASS_MATERIALS } from '@config/glass';
import { useGlassMode } from '@stores/index';
import type { GlassVariant } from '@typings/glass';
import { cx } from '@utils/cx';

import { LiquidFilter } from './LiquidFilter';
import styles from './glass.module.scss';
import { useLiquidFilter } from './useLiquidFilter';
import { usePointerGlare } from './usePointerGlare';

export interface GlassOptions {
  readonly variant?: GlassVariant;
  /** Adds hover lift, press feedback and the pointer-following highlight. */
  readonly interactive?: boolean;
  /**
   * Opts this surface out of liquid refraction while keeping frost. For
   * large surfaces where the rim effect would be off-screen anyway, or
   * where many repeat in view and the GPU cost adds up.
   */
  readonly refract?: boolean;
  readonly className?: string | undefined;
  readonly style?: CSSProperties | undefined;
}

type GlassStyle = CSSProperties & Record<`--${string}`, string>;

export interface GlassBinding {
  readonly className: string;
  readonly style: GlassStyle;
  /** Render inside the element. Null unless liquid refraction is active. */
  readonly filter: ReactNode;
}

/**
 * Turns any element into a glass surface.
 *
 * The caller owns the element's ref and passes it in, keeping the ref out
 * of the returned object: everything returned here is read during render,
 * and a ref must not be.
 *
 * A hook rather than a wrapper component, so the element keeps its own
 * semantics: a link stays an <a>, a list item stays an <li>, with no extra
 * div in the accessibility tree. GlassSurface and GlassButton are thin
 * shells over this.
 *
 * Material values reach CSS as custom properties from GLASS_MATERIALS, so
 * the liquid filter and the frost fallback can never disagree about how
 * thick a variant is.
 */
export const useGlass = (
  ref: RefObject<HTMLElement | null>,
  { variant = 'regular', interactive = false, refract = true, className, style }: GlassOptions,
): GlassBinding => {
  const mode = useGlassMode();
  const material = GLASS_MATERIALS[variant];

  const liquid = useLiquidFilter(ref, material, mode === 'liquid' && refract);
  usePointerGlare(ref, interactive && mode !== 'solid');

  const glassStyle = useMemo<GlassStyle>(() => {
    const blur = `blur(${String(material.blur)}px)`;
    const saturate = `saturate(${String(material.saturation)})`;
    return {
      ...style,
      '--glass-frost': `${blur} ${saturate}`,
      /* Refraction sits between the native blur and saturation; see
         LiquidFilter for why it is split this way. */
      ...(liquid ? { '--glass-filter': `${blur} url(#${liquid.id}) ${saturate}` } : {}),
    };
  }, [style, material, liquid]);

  return {
    className: cx(styles.glass, styles[variant], interactive && styles.interactive, className),
    style: glassStyle,
    filter: liquid ? <LiquidFilter state={liquid} material={material} /> : null,
  };
};
